"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import {
  buildHotpleKoreaStyle,
  CLUSTER_COUNT_LAYER_ID,
  CLUSTER_LAYER_ID,
  getHotpleMarkerColor,
  MAP_COLORS,
  PLACE_LAYER_ID,
  PLACES_SOURCE_ID,
  PLACE_LABEL_LAYER_ID,
} from "./hotple-korea-style";
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  MAPLIBRE_PM_TILES_URL,
  MAPLIBRE_WORKER_URL,
  normalizeMapColor,
} from "./config";
import {
  installSubwayLayers,
  pickStationLabels,
} from "./subway-network-layers";

const DEFAULT_MARKER_COLOR = MAP_COLORS.hotpleMain;
const DEFAULT_SELECTED_MARKER_COLOR = MAP_COLORS.hotpleSelected;
const DEFAULT_CLUSTER_COLOR = MAP_COLORS.hotpleMain;
const MIN_CLUSTER_CLICK_ZOOM_STEP = 2;
const TOUCH_CLICK_SUPPRESSION_MS = 500;
// 검색 결과에서 먼 곳(예: 서울에서 대구)을 고르면 지도가 그 사이를 날아가면서
// 경로 위의 타일을 전부 불러온다. 시간이 오래 걸리고 화면이 한참 흐르는 것처럼 보인다.
// 일정 거리 이상이면 애니메이션 없이 바로 옮긴다(2026-07-31 대표님 제보).
const CAMERA_JUMP_DISTANCE_KM = 30;

const getRoughDistanceKm = (from, to) => {
  const lat1 = Number(from?.lat);
  const lng1 = Number(from?.lng);
  const lat2 = Number(to?.lat ?? to?.[1]);
  const lng2 = Number(to?.lng ?? to?.[0]);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return 0;

  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const isFarCameraMove = (map, target) => {
  try {
    const current = map?.getCenter?.();
    if (!current) return false;
    return getRoughDistanceKm(current, target) > CAMERA_JUMP_DISTANCE_KM;
  } catch {
    return false;
  }
};

const CAMERA_CENTER_EPSILON = 0.000001;
const CAMERA_ZOOM_EPSILON = 0.001;
const CAMERA_ECHO_WINDOW_MS = 1000;
const CAMERA_ECHO_HISTORY_LIMIT = 40;
const CLUSTER_INTERACTIVE_LAYER_IDS = [CLUSTER_COUNT_LAYER_ID, CLUSTER_LAYER_ID];
const POINTER_LAYER_IDS = [
  PLACE_LAYER_ID,
  CLUSTER_LAYER_ID,
  CLUSTER_COUNT_LAYER_ID,
];
const TRACKED_LOCATION_OVERLAY_OFFSET = [0, -24];

let workerConfigured = false;
let sharedProtocol = null;
let protocolRegistered = false;

const configureMaplibreWorker = () => {
  if (workerConfigured) return;

  maplibregl.setWorkerUrl(MAPLIBRE_WORKER_URL);
  workerConfigured = true;
};

const registerPmtilesProtocol = () => {
  if (protocolRegistered) return;

  sharedProtocol = sharedProtocol || new Protocol();

  try {
    maplibregl.addProtocol("pmtiles", sharedProtocol.tile);
  } catch (error) {
    const message = String(error?.message || error);
    if (!message.toLowerCase().includes("already")) throw error;
  }

  protocolRegistered = true;
};

const toLngLatArray = (value, fallback = DEFAULT_MAP_CENTER) => {
  const lat = Number(value?.lat ?? value?.latitude ?? fallback.lat);
  const lng = Number(value?.lng ?? value?.longitude ?? fallback.lng);

  return [lng, lat];
};

const toOptionalLngLatArray = (value) => {
  const lat = Number(value?.lat ?? value?.latitude);
  const lng = Number(value?.lng ?? value?.longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lng, lat];
};

/**
 * 아래에서 끌어올리는 목록(시트)이 지도의 아래쪽을 덮는다. 그래서 어떤 좌표를
 * "가운데" 에 놓으면 실제로는 **시트에 거의 닿는 자리**에 찍힌다 — 가게 화면에서
 * 지도로 넘어왔을 때 핀이 아래 모서리에 박혀 보이던 이유다(2026-08-10 사장님 제보).
 *
 * 그래서 카메라를 옮길 때 목표를 덮인 높이의 절반만큼 위로 올린다. 시트가 없는
 * 화면(상세의 작은 지도 등)에서는 0 이라 아무 일도 일어나지 않는다.
 */
const getSheetCameraOffset = (map) => {
  try {
    if (typeof document === "undefined") return [0, 0];
    const sheet = document.querySelector("[data-places-sheet]");
    if (!sheet) return [0, 0];
    const rect = map.getContainer().getBoundingClientRect();
    const covered = rect.bottom - sheet.getBoundingClientRect().top;
    if (!(covered > 60)) return [0, 0];
    // 시트를 끝까지 올린 상태에서 목표가 화면 밖으로 나가지 않게 상한을 둔다.
    const capped = Math.min(covered, rect.height * 0.45);
    return [0, -capped / 2];
  } catch (error) {
    return [0, 0];
  }
};

const shouldUpdateMapCamera = (map, center, zoom) => {
  const [lng, lat] = center || [];
  const nextLng = Number(lng);
  const nextLat = Number(lat);
  const nextZoom = Number(zoom);

  if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return false;

  const currentCenter = map.getCenter();
  const currentZoom = map.getZoom();

  return (
    Math.abs(currentCenter.lng - nextLng) > CAMERA_CENTER_EPSILON ||
    Math.abs(currentCenter.lat - nextLat) > CAMERA_CENTER_EPSILON ||
    (Number.isFinite(nextZoom) &&
      Math.abs(currentZoom - nextZoom) > CAMERA_ZOOM_EPSILON)
  );
};

const isSameCameraState = (camera, center, zoom) => {
  const [lng, lat] = center || [];
  const nextLng = Number(lng);
  const nextLat = Number(lat);
  const nextZoom = Number(zoom);

  if (
    !camera ||
    !Number.isFinite(nextLat) ||
    !Number.isFinite(nextLng) ||
    !Number.isFinite(nextZoom)
  ) {
    return false;
  }

  return (
    Math.abs(camera.center.lng - nextLng) <= CAMERA_CENTER_EPSILON &&
    Math.abs(camera.center.lat - nextLat) <= CAMERA_CENTER_EPSILON &&
    Math.abs(camera.zoom - nextZoom) <= CAMERA_ZOOM_EPSILON
  );
};

const rememberEmittedCamera = (historyRef, camera) => {
  historyRef.current = [
    ...historyRef.current,
    {
      camera,
      emittedAt: Date.now(),
    },
  ].slice(-CAMERA_ECHO_HISTORY_LIMIT);
};

const isCameraEchoUpdate = (cameraHistory, center, zoom) => {
  const now = Date.now();

  return cameraHistory.some(
    ({ camera, emittedAt }) =>
      now - emittedAt <= CAMERA_ECHO_WINDOW_MS &&
      isSameCameraState(camera, center, zoom)
  );
};

const getMarkerId = (marker, index) =>
  String(
    marker?.id ||
      marker?._id ||
      marker?.placeId ||
      marker?.placeUniqueNumber ||
      marker?.contentid ||
      marker?.contentId ||
      index
  );

const getMarkerName = (marker, index) =>
  marker?.name ||
  marker?.properties?.name ||
  marker?.address ||
  `Place ${index + 1}`;

const markerToFeature = (marker, index, markerColor) => {
  const geometryCoordinates = marker?.geometry?.coordinates;
  const coordinates = Array.isArray(geometryCoordinates)
    ? geometryCoordinates
    : toLngLatArray(marker, DEFAULT_MAP_CENTER);
  const lng = Number(coordinates?.[0]);
  const lat = Number(coordinates?.[1]);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const id = getMarkerId(marker, index);
  const markerType =
    marker?.type || marker?.properties?.type || marker?.place?.type || "";
  const markerCategory =
    marker?.category ||
    marker?.subcategory ||
    marker?.properties?.category ||
    marker?.place?.category ||
    marker?.place?.subcategory ||
    markerType;
  const explicitColor = normalizeMapColor(
    marker?.properties?.color || marker?.color,
    ""
  );
  const categoryColor =
    explicitColor ||
    getHotpleMarkerColor([markerType, markerCategory].join(" "), markerColor);
  const markerPlaceIds = [
    id,
    marker?._id,
    marker?.id,
    marker?.placeId,
    marker?.placeUniqueNumber,
    marker?.publicId,
    marker?.contentid,
    marker?.contentId,
    ...(Array.isArray(marker?.placeIds) ? marker.placeIds : []),
    ...(Array.isArray(marker?.properties?.placeIds)
      ? marker.properties.placeIds
      : []),
  ]
    .filter((value) => value != null && value !== "")
    .map(String);

  return {
    type: "Feature",
    id,
    geometry: {
      type: "Point",
      coordinates: [lng, lat],
    },
    properties: {
      id,
      index: Number(marker?.index || index + 1),
      name: getMarkerName(marker, index),
      type: markerType,
      category: markerCategory,
      color: categoryColor,
      placeIds: Array.from(new Set(markerPlaceIds)),
      selected: false,
      isSearchResultPin: Boolean(
        marker?.isSearchResultPin || marker?.properties?.isSearchResultPin
      ),
    },
  };
};

const getFeaturePlaceIds = (feature) => [
  feature?.properties?.id,
  ...(Array.isArray(feature?.properties?.placeIds)
    ? feature.properties.placeIds
    : []),
];

const isFeatureSelected = (feature, selectedMarkerId) => {
  if (!selectedMarkerId) return false;
  return getFeaturePlaceIds(feature).some(
    (id) => String(id) === String(selectedMarkerId)
  );
};

// 가까이 있는 핀을 묶는 거리(m).
// 한 건물(백화점·시장) 안 가게들은 좌표가 같거나 몇 m 안에 흩어져 있어서,
// 확대하면 한 자리에 수십~수백 개가 겹쳐 **동그란 뭉치**처럼 보였다
// (2026-08-15 대표님 신고). 그 정도 거리는 사람에게 「같은 자리」다.
// 지도 자체 묶기가 화면상 거리로 처리하므로, 우리는 **아무리 확대해도 안 갈라지는**
// 경우만 맡는다 — 좌표가 완전히 같은 것들(한 건물 가게가 건물 중심 좌표 하나로 들어온 경우).
const STACK_RADIUS_M = 1;
const stackKeyOf = (feature) => {
  const [lng, lat] = feature?.geometry?.coordinates || [];
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  // 위도 1도 ≈ 111km. 경도는 위도에 따라 줄어든다.
  const latStep = STACK_RADIUS_M / 111000;
  const lngStep = latStep / Math.max(Math.cos((lat * Math.PI) / 180), 0.2);
  return `${Math.round(lng / lngStep)}:${Math.round(lat / latStep)}`;
};

/**
 * 완전히 같은 자리에 있는 핀들을 **하나로 합친다.**
 * 대표는 그 자리에서 가장 앞선 것(목록 순서)이고, 나머지 id 는 placeIds 에 모은다.
 * 합친 개수는 stackCount 로 남겨 지도에 작게 표시한다.
 */
const stackSamePlaceFeatures = (features) => {
  const byKey = new Map();
  const out = [];
  for (const feature of features) {
    const key = stackKeyOf(feature);
    if (!key) {
      out.push(feature);
      continue;
    }
    const found = byKey.get(key);
    if (!found) {
      const copy = {
        ...feature,
        properties: { ...feature.properties, stackCount: 1 },
      };
      byKey.set(key, copy);
      out.push(copy);
      continue;
    }
    found.properties.stackCount += 1;
    const merged = new Set([
      ...(found.properties.placeIds || []),
      ...(feature.properties?.placeIds || []),
    ]);
    found.properties.placeIds = Array.from(merged);
  }
  return out;
};

const buildPlacesGeoJson = (
  markers,
  selectedMarkerId,
  markerColor = DEFAULT_MARKER_COLOR
) => ({
  type: "FeatureCollection",
  features: stackSamePlaceFeatures(
    (Array.isArray(markers) ? markers : [])
      .map((marker, index) => markerToFeature(marker, index, markerColor))
      .filter(Boolean)
  ).map((feature) => ({
    ...feature,
    properties: {
      ...feature.properties,
      selected: isFeatureSelected(feature, selectedMarkerId),
    },
  })),
});

const getGeoJsonCoordinates = (geoJson) =>
  (Array.isArray(geoJson?.features) ? geoJson.features : [])
    .map((feature) => feature?.geometry?.coordinates)
    .filter(
      (coordinates) =>
        Array.isArray(coordinates) &&
        Number.isFinite(Number(coordinates[0])) &&
        Number.isFinite(Number(coordinates[1]))
    )
    .map((coordinates) => [Number(coordinates[0]), Number(coordinates[1])]);

const getCoordinatesKey = (coordinates) =>
  coordinates
    .map(([lng, lat]) => `${lng.toFixed(7)},${lat.toFixed(7)}`)
    .join("|");

const getMapBounds = (map) => {
  const bounds = map.getBounds();

  return {
    south: bounds.getSouth(),
    west: bounds.getWest(),
    north: bounds.getNorth(),
    east: bounds.getEast(),
  };
};

const getMapCameraState = (map, extra = {}) => {
  const center = map.getCenter();

  return {
    center: {
      lat: center.lat,
      lng: center.lng,
    },
    zoom: map.getZoom(),
    bounds: getMapBounds(map),
    ...extra,
  };
};

const getClusterEventPoint = (event) => event?.point || event?.points?.[0] || null;

const getRenderedClusterFeature = (map, event) => {
  const point = getClusterEventPoint(event);
  if (!point) return null;

  return (
    map.queryRenderedFeatures(point, {
      layers: CLUSTER_INTERACTIVE_LAYER_IDS,
    })?.[0] || null
  );
};

const getClusterTargetZoom = (map, expansionZoom) =>
  Math.min(
    // Always zoom in decisively and past the clustering ceiling (clusterMaxZoom
    // is 16) so a cluster tap visibly breaks apart into individual pins.
    Math.max(expansionZoom, map.getZoom() + MIN_CLUSTER_CLICK_ZOOM_STEP, 17.5),
    map.getMaxZoom()
  );

const isTouchMapEvent = (event) =>
  event?.originalEvent?.type?.startsWith("touch");

const isSyntheticClickAfterTouch = (event, lastTouchTime) =>
  event?.originalEvent?.type === "click" &&
  Date.now() - lastTouchTime < TOUCH_CLICK_SUPPRESSION_MS;

export default function MapLibreMap({
  className = "",
  style,
  pmtilesUrl,
  mapCenter,
  mapZoom = DEFAULT_MAP_ZOOM,
  markers,
  selectedMarkerId,
  markerColor = DEFAULT_MARKER_COLOR,
  selectedMarkerColor = DEFAULT_SELECTED_MARKER_COLOR,
  clusterColor = DEFAULT_CLUSTER_COLOR,
  onCameraChange,
  onIdle,
  onInitialCameraReady,
  onUserMoveEnd,
  onMarkerClick,
  cameraJumpKey = 0,
  cameraAnimationDuration = 350,
  scrollZoom = true,
  // 손가락에 반응하지 않는 지도(상세 화면의 작은 지도). 스크롤을 뺏지 않는다.
  interactive = true,
  showNavigationControl = true,
  areaLabels = null,
  areaLabelZoomMax = 15.5,
  // 이름표를 눌렀을 때 갈 확대. 안 주면 예전처럼 (상한 + 1.6).
  // 큰 이름은 세부 단계로, 세부는 핀 단계로 가야 해서 밖에서 정한다.
  areaLabelClickZoom = null,
  onAreaLabelClick = null,
  // 확대했을 때 얹는 작은 이름표(읽는 것). [{n, lat, lng}]
  landmarkLabels = null,
  // 전국 지하철 한 벌 — 노선 선·역사·출구. 화면 범위로 묻지 않고 파일로 한 번 받는다.
  subwayNetwork = null,
  clusterMarkers = true,
  // 저장 목록처럼 '내가 고른 몇 곳'을 보는 지도에서는 줌과 상관없이 이름을 보인다.
  // 기본은 꺼 둔다 — 동네 지도(/places)는 핀이 수천 개라 이름을 다 띄우면 못 읽는다.
  alwaysShowPlaceLabels = false,
  trackedLocation,
  trackedLocationOverlayElement = null,
  trackedLocationOverlayOffset = TRACKED_LOCATION_OVERLAY_OFFSET,
  focusTrackedLocationOnChange = false,
  trackedLocationFocusZoom,
  trackedLocationFocusPadding = null,
  fitBoundsToMarkers = false,
  fitBoundsPadding = 96,
  fitBoundsMaxZoom,
  fitBoundsRequestKey = "",
  syncCameraOnMove = true,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const idleTimerRef = useRef(null);
  const userInteractionRef = useRef(false);
  // 지도 캔버스에 직접 건 손짓 리스너를 걷어낼 때 쓴다.
  const gestureCleanupRef = useRef(null);
  const trackedLocationOverlayMarkerRef = useRef(null);
  const [mapError, setMapError] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const resolvedMarkerColor = normalizeMapColor(markerColor, DEFAULT_MARKER_COLOR);
  const resolvedSelectedMarkerColor = normalizeMapColor(
    selectedMarkerColor,
    DEFAULT_SELECTED_MARKER_COLOR
  );
  const resolvedClusterColor = normalizeMapColor(clusterColor, DEFAULT_CLUSTER_COLOR);
  const markersGeoJson = useMemo(
    () => buildPlacesGeoJson(markers, selectedMarkerId, resolvedMarkerColor),
    [markers, resolvedMarkerColor, selectedMarkerId]
  );
  // Keep the built pin features in a ref so the map click handler (bound once)
  // can hit-test them from OUR data — independent of MapLibre's worker-generated
  // tiles, which lag several seconds behind the basemap parse on a new area
  // (that's why a visible pin isn't tappable for ~5-7s: queryRenderedFeatures is
  // empty until the pin source tiles are built). See the click fallback below.
  const markersGeoJsonRef = useRef(markersGeoJson);
  useEffect(() => {
    markersGeoJsonRef.current = markersGeoJson;
  }, [markersGeoJson]);
  const fitBoundsCoordinates = useMemo(
    () => getGeoJsonCoordinates(markersGeoJson),
    [markersGeoJson]
  );
  const fitBoundsCoordinatesKey = useMemo(
    () => getCoordinatesKey(fitBoundsCoordinates),
    [fitBoundsCoordinates]
  );
  const resolvedCenter = useMemo(() => toLngLatArray(mapCenter), [mapCenter]);
  const resolvedTrackedLocation = useMemo(
    () => toOptionalLngLatArray(trackedLocation),
    [trackedLocation]
  );
  const resolvedPmtilesUrl = pmtilesUrl || MAPLIBRE_PM_TILES_URL;

  const callbacksRef = useRef({
    onCameraChange,
    onIdle,
    onInitialCameraReady,
    onUserMoveEnd,
    onMarkerClick,
    areaLabels,
    areaLabelZoomMax,
    areaLabelClickZoom,
    onAreaLabelClick,
    landmarkLabels,
    subwayNetwork,
  });
  const areaMarkersRef = useRef([]);
  // 확대했을 때 뜨는 작은 이름표(읽는 것). 상권 칩과 따로 관리한다.
  const landmarkMarkersRef = useRef([]);
  // 역 이름표(흰 알약 + 호선 색 동그라미). 출구·선·역사는 GL 레이어가 그린다.
  const stationMarkersRef = useRef([]);
  // 이름표를 다시 그리는 함수를 담아 둔다. 지도가 움직일 때 말고
  // **목록이 바뀌었을 때도** 다시 그려야 한다(아래 useEffect).
  const syncAreaLabelsRef = useRef(null);
  // 지금 지도를 끌고 있는가 — 그동안에는 이름표를 적게 그린다
  const areaLabelsMovingRef = useRef(false);
  const initialMapOptionsRef = useRef(null);
  const emittedCameraHistoryRef = useRef([]);
  const lastCameraJumpKeyRef = useRef(cameraJumpKey);
  const focusedTrackedLocationRef = useRef(null);
  const syncCameraOnMoveRef = useRef(syncCameraOnMove);

  callbacksRef.current = {
    onCameraChange,
    onIdle,
    onInitialCameraReady,
    onUserMoveEnd,
    onMarkerClick,
    areaLabels,
    areaLabelZoomMax,
    areaLabelClickZoom,
    onAreaLabelClick,
    landmarkLabels,
    subwayNetwork,
  };
  syncCameraOnMoveRef.current = syncCameraOnMove;

  if (!initialMapOptionsRef.current) {
    initialMapOptionsRef.current = {
      center: resolvedCenter,
      clusterMarkers,
      clusterColor: resolvedClusterColor,
      markerColor: resolvedMarkerColor,
      markersGeoJson,
      pmtilesUrl: resolvedPmtilesUrl,
      scrollZoom,
      interactive,
      selectedMarkerColor: resolvedSelectedMarkerColor,
      zoom: mapZoom || DEFAULT_MAP_ZOOM,
    };
  }

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    configureMaplibreWorker();
    registerPmtilesProtocol();

    const initialMapOptions = initialMapOptionsRef.current;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildHotpleKoreaStyle({
        pmtilesUrl: initialMapOptions.pmtilesUrl,
        markersGeoJson: initialMapOptions.markersGeoJson,
        markerColor: initialMapOptions.markerColor,
        selectedMarkerColor: initialMapOptions.selectedMarkerColor,
        clusterColor: initialMapOptions.clusterColor,
        clusterMarkers: initialMapOptions.clusterMarkers,
      }),
      center: initialMapOptions.center,
      zoom: initialMapOptions.zoom,
      maxZoom: 19,
      minZoom: 5,
      scrollZoom: initialMapOptions.scrollZoom,
      interactive: initialMapOptions.interactive,
      dragPan: {
        deceleration: 10000,
        maxSpeed: 500,
      },
      renderWorldCopies: false,
      attributionControl: false,
    });
    const resizeMap = () => map.resize();
    const resizeFrame = window.requestAnimationFrame(resizeMap);
    const resizeTimeout = window.setTimeout(resizeMap, 250);
    const resizeObserver = new ResizeObserver(resizeMap);
    resizeObserver.observe(containerRef.current);

    mapRef.current = map;
    const initialCameraFrame = window.requestAnimationFrame(() => {
      callbacksRef.current.onInitialCameraReady?.(getMapCameraState(map));
    });
    if (showNavigationControl) {
      map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }));
    }
    map.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      "bottom-right"
    );

    map.on("load", () => {
      setIsLoaded(true);
      // 처음 뜰 때의 중심은 지도를 만들 때 정해지므로 아래 카메라 보정을 타지 않는다.
      // 그래서 시트가 붙은 뒤 한 번만 다시 맞춘다 — 가게 화면에서 넘어왔을 때 그 가게가
      // 시트에 가려 아래 모서리에 박히던 것을 고친다(2026-08-10 사장님 제보).
      window.setTimeout(() => {
        try {
          if (userInteractionRef.current) return;
          const offset = getSheetCameraOffset(map);
          if (!offset[1]) return;
          map.easeTo({
            center: map.getCenter(),
            zoom: map.getZoom(),
            offset,
            duration: 0,
          });
        } catch (error) {
          // 못 맞춰도 지도는 그대로 쓴다.
        }
      }, 400);
      // 이름표 레이어는 줌 15 부터 뜬다. 저장한 곳이 흩어져 있으면 지도가 그보다
      // 멀리 맞춰져 이름이 하나도 안 나온다 — 그 화면에서만 하한을 푼다.
      if (alwaysShowPlaceLabels) {
        try {
          if (map.getLayer(PLACE_LABEL_LAYER_ID)) {
            map.setLayerZoomRange(PLACE_LABEL_LAYER_ID, 0, 24);
          }
        } catch (error) {
          // 이름표가 안 떠도 지도는 살아 있어야 한다.
        }
      }
    });
    map.on("error", (event) => {
      const message =
        event?.error?.message ||
        event?.error?.statusText ||
        "MapLibre map failed to load.";
      setMapError(String(message));
    });

    const markUserInteraction = (event) => {
      if (event?.originalEvent) userInteractionRef.current = true;
    };

    map.on("dragstart", markUserInteraction);
    map.on("zoomstart", markUserInteraction);
    map.on("rotatestart", markUserInteraction);
    map.on("pitchstart", markUserInteraction);
    map.on("boxzoomstart", markUserInteraction);

    // maplibre 가 알려 주는 zoomstart 에는 originalEvent 가 없을 때가 있다.
    // 특히 **휠 확대**가 그렇다 — 그래서 확대해도 목록·핀을 다시 부르지 않았다.
    // maplibre 가 뭐라고 하든, 지도 위에서 실제로 손을 댔으면 사용자 조작으로 본다.
    // 프로그램이 옮기는 카메라(fitBounds 등)는 이 이벤트가 없으므로 영향받지 않는다.
    const markUserGestureFromDom = () => {
      userInteractionRef.current = true;
    };
    const gestureTarget = map.getCanvasContainer?.() || map.getContainer?.();
    if (gestureTarget) {
      gestureTarget.addEventListener("wheel", markUserGestureFromDom, { passive: true });
      gestureTarget.addEventListener("pointerdown", markUserGestureFromDom, { passive: true });
      gestureTarget.addEventListener("touchstart", markUserGestureFromDom, { passive: true });
      gestureCleanupRef.current = () => {
        gestureTarget.removeEventListener("wheel", markUserGestureFromDom);
        gestureTarget.removeEventListener("pointerdown", markUserGestureFromDom);
        gestureTarget.removeEventListener("touchstart", markUserGestureFromDom);
      };
    }

    map.on("move", () => {
      const camera = getMapCameraState(map);
      rememberEmittedCamera(emittedCameraHistoryRef, camera);
      if (syncCameraOnMoveRef.current) {
        callbacksRef.current.onCameraChange?.(camera);
      }
    });

    map.on("moveend", () => {
      if (!userInteractionRef.current) return;

      callbacksRef.current.onUserMoveEnd?.(
        getMapCameraState(map, { isUserInteraction: true })
      );
    });

    map.on("idle", () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

      idleTimerRef.current = setTimeout(() => {
        const isUserInteraction = userInteractionRef.current;
        userInteractionRef.current = false;

        callbacksRef.current.onIdle?.(
          getMapCameraState(map, { isUserInteraction })
        );
      }, 100);
    });

    // ---- Area-name pill labels (opt-in via the `areaLabels` prop) ----
    // At far zoom, hide the GL place/cluster layers and render one HTML pill per
    // nearby station ("역이름 count", style A) with greedy overlap-skipping.
    // Reverts to GL pins/clusters past the zoom threshold. Fully isolated behind
    // the prop so the other MapLibreMap usages are untouched.
    if (callbacksRef.current.areaLabels) {
      if (typeof document !== "undefined" && !document.getElementById("hotple-area-pill-style")) {
        const st = document.createElement("style");
        st.id = "hotple-area-pill-style";
        st.textContent =
          ".hotple-area-pill{display:inline-flex;align-items:center;gap:5px;white-space:nowrap;background:#fff;border:1px solid rgba(0,0,0,.08);border-radius:999px;box-shadow:0 1px 2px rgba(0,0,0,.12);padding:4px 10px;font-size:12px;font-weight:700;color:#1c1a1c;cursor:pointer;line-height:1.2;font-family:'Pretendard','Apple SD Gothic Neo','Malgun Gothic',sans-serif;}" +
          ".hotple-area-pill:active{transform:scale(.94);}" +
          // 다크 모드용 예외를 두지 않는다. 핫플은 밝은 앱이고 지도도 밝은데 이름표만
          // 어두워지면 그것만 떠 보인다(2026-08-06 사장님 화면에서 검은 칩으로 나왔다 —
          // 컴퓨터가 다크 모드였기 때문이다).
          ".hotple-area-pill .hap-cnt{color:#e5136f;font-weight:800;}" +
          // 작은 이름표 — 배경 없이 흰 테두리만 둘러 지도에서 뜨게 한다.
          // 누르는 것이 아니므로 pointer-events 를 끈다(밑의 핀을 가리면 안 된다).
          ".hotple-landmark-label{white-space:nowrap;font-size:11.5px;font-weight:800;color:#2b2630;" +
          "letter-spacing:-.02em;pointer-events:none;line-height:1.2;" +
          "text-shadow:0 0 3px #fff,0 0 3px #fff,0 0 3px #fff,0 0 2px #fff,0 1px 2px rgba(255,255,255,.95);" +
          "font-family:'Pretendard','Apple SD Gothic Neo','Malgun Gothic',sans-serif;}" +
          // 역 이름표 — 흰 알약에 호선 색 동그라미. 출구 번호만 떠 있으면 그게 출구인지
          // 알 수 없다는 지적(2026-08-10)에 따라, 역이 보일 때만 출구가 같이 뜬다.
          ".hotple-subway-station{display:inline-flex;align-items:center;gap:3px;background:#fff;border-radius:999px;padding:2px 8px 2px 2px;font-size:11px;font-weight:800;color:#1f1b24;white-space:nowrap;pointer-events:none;line-height:1.2;box-shadow:0 1px 4px rgba(0,0,0,.22);font-family:'Pretendard','Apple SD Gothic Neo','Malgun Gothic',sans-serif;}" +
          ".hotple-subway-station .hss-ln{width:15px;height:15px;border-radius:50%;color:#fff;font-size:9.5px;display:grid;place-items:center;font-weight:800;}";
        document.head.appendChild(st);
      }
      const clearStationMarkers = () => {
        stationMarkersRef.current.forEach((m) => m.remove());
        stationMarkersRef.current = [];
      };

      // 역 이름표. 자리 고르기(겹치면 버리고, 시트에 가리면 안 그린다)는
      // subway-network-layers 가 계산해 주고 여기서는 붙이기만 한다.
      const drawStationLabels = (maxY) => {
        const data = callbacksRef.current.subwayNetwork;
        if (!data) return;
        pickStationLabels(map, data, maxY).forEach((item) => {
          const el = document.createElement("div");
          el.className = "hotple-subway-station";
          // 환승역은 호선 동그라미를 나란히 단다(서울역 = 1·4·공항철도·경의중앙).
          (item.dots || []).forEach((d) => {
            const dot = document.createElement("i");
            dot.className = "hss-ln";
            dot.style.background = d.c;
            dot.textContent = d.b || "";
            el.appendChild(dot);
          });
          const nm = document.createElement("span");
          nm.textContent = item.n;
          el.appendChild(nm);
          const mk = new maplibregl.Marker({ element: el, anchor: "center" })
            .setLngLat([item.lng, item.lat])
            .addTo(map);
          stationMarkersRef.current.push(mk);
        });
      };
      const clearLandmarkMarkers = () => {
        landmarkMarkersRef.current.forEach((m) => m.remove());
        landmarkMarkersRef.current = [];
      };

      // 핀이 보이는 단계에서만 뜬다. 한 화면에 다섯 개까지, 겹치면 버린다.
      // 숫자를 붙이지 않는다 — 랜드마크는 반경이 서로 겹쳐서 숫자를 더하면 같은 가게를
      // 두 번 세게 된다(상권 칩은 격자로 나눠서 안 겹친다).
      // ⚠️ 아래에서 끌어올리는 목록(시트)에 가리는 자리에는 그리지 않는다.
      // 시트는 지도 위에 덮이므로, 거기 그리면 반쯤 잘려 보이거나 아예 안 보인다.
      // 시트가 지금 어디까지 올라와 있는지 그때그때 물어본다(끌어올리면 값이 바뀐다).
      const labelMaxY = () => {
        const canvasRect = map.getContainer().getBoundingClientRect();
        const sheet = typeof document !== "undefined"
          ? document.querySelector("[data-places-sheet]")
          : null;
        const sheetTop = sheet ? sheet.getBoundingClientRect().top : Infinity;
        return Number.isFinite(sheetTop)
          ? sheetTop - canvasRect.top - 14
          : canvasRect.height;
      };

      const drawLandmarkLabels = (maxY) => {
        const list = callbacksRef.current.landmarkLabels;
        if (!list || !list.length) return;
        const bounds = map.getBounds();
        const placed = [];
        for (const item of list) {
          if (placed.length >= 5) break;
          if (!item || !Number.isFinite(item.lat) || !Number.isFinite(item.lng)) continue;
          if (!bounds.contains([item.lng, item.lat])) continue;

          const pt = map.project([item.lng, item.lat]);
          if (pt.y > maxY) continue;
          const halfW = 3.5 * String(item.n || "").length + 8;
          const box = { x1: pt.x - halfW, y1: pt.y - 11, x2: pt.x + halfW, y2: pt.y + 11 };
          if (placed.some((q) => !(box.x2 < q.x1 || box.x1 > q.x2 || box.y2 < q.y1 || box.y1 > q.y2))) continue;
          placed.push(box);

          const el = document.createElement("div");
          el.className = "hotple-landmark-label";
          el.textContent = item.n;
          const mk = new maplibregl.Marker({ element: el, anchor: "center" })
            .setLngLat([item.lng, item.lat])
            .addTo(map);
          landmarkMarkersRef.current.push(mk);
        }
      };

      const clearAreaMarkers = () => {
        areaMarkersRef.current.forEach((m) => m.remove());
        areaMarkersRef.current = [];
      };
      const toggleGlPlaceLayers = (visible) => {
        const style = map.getStyle();
        if (!style || !style.layers) return;
        style.layers.forEach((l) => {
          if (l.id && l.id.indexOf("hotple-place") === 0) {
            try {
              map.setLayoutProperty(l.id, "visibility", visible ? "visible" : "none");
            } catch (e) {}
          }
        });
      };
      const syncAreaLabels = () => {
        const stations = callbacksRef.current.areaLabels;
        // 이 지도가 상권 이름표를 아예 안 쓰는 경우(다른 화면들) — 손대지 않는다.
        if (!stations) return;

        // ⚠️ 빈 배열은 "안 쓴다" 가 아니라 **"지금 배율에는 이름표가 없다"** 이다.
        // 예전에는 빈 배열에서도 그냥 빠져나가서, 확대해 들어가도 이름표가 그대로 남고
        // 핀 레이어가 숨겨진 채로 있었다(2026-08-07: 칩을 눌러 확대하면 칩만 남고
        // 핀이 안 보이던 것). 이 경우에는 이름표를 지우고 핀을 다시 켜야 한다.
        const zoomMax = callbacksRef.current.areaLabelZoomMax ?? 15.5;
        const showAreas = stations.length > 0 && map.getZoom() < zoomMax;
        clearAreaMarkers();
        clearLandmarkMarkers();
        clearStationMarkers();
        if (!showAreas) {
          toggleGlPlaceLayers(true);
          // 칩이 사라지는 단계부터 작은 이름표가 대신 뜬다. 둘이 같이 뜨면 시끄럽다.
          // ⚠️ 역 이름표는 **따로** 부른다 — 작은 이름표가 하나도 없는 자리(가산 등)에서
          //    작은 이름표 함수가 앞에서 끝나 버려 역 이름이 통째로 안 뜬 적이 있다.
          const maxY = labelMaxY();
          drawLandmarkLabels(maxY);
          drawStationLabels(maxY);
          return;
        }
        const b = map.getBounds();
        const inBounds = stations
          .filter((s) => s.c > 0 && b.contains([s.lng, s.lat]))
          .sort((a, z) => z.c - a.c);
        if (inBounds.length === 0) {
          // No area coverage in view (e.g. non-Seoul like 포항) — DON'T hide the
          // pins; keep the GL pins/clusters so the map is never empty when places
          // exist. Area pills only take over where we actually have station data.
          toggleGlPlaceLayers(true);
          return;
        }
        toggleGlPlaceLayers(false);
        // 상권 칩이 뜨는 단계에서도 역 이름표는 그린다 — 노선 선은 이미 깔려 있는데
        // 역 이름이 없으면 그 선이 무슨 노선인지 알 수 없다.
        drawStationLabels(labelMaxY());
        // 움직이는 동안만 적게, 멈추면 다 보여 준다.
        //
        // 이름표는 HTML 조각이라 지도를 움직이는 **매 프레임마다** 위치를 다시 잡는다.
        // 그래픽 가속이 없는 컴퓨터에서는 그 비용이 그대로 CPU 로 간다(2026-08-07 사장님
        // 크롬: WebGL Software only). 그렇다고 개수를 아예 줄이면 보여줄 것이 줄어든다 —
        // 그건 성능 문제를 내용으로 갚는 것이라 잘못된 거래다.
        //
        // 읽는 것은 멈췄을 때다. 그래서 끄는 대신 **끌고 있는 동안만** 몇 개로 줄인다.
        const MAX_PILLS = areaLabelsMovingRef.current ? 8 : 60;
        const placed = [];
        for (const s of inBounds) {
          if (placed.length >= MAX_PILLS) break;
          const pt = map.project([s.lng, s.lat]);
          const halfW = 6 * String(s.n).length + 30;
          const box = { x1: pt.x - halfW, y1: pt.y - 16, x2: pt.x + halfW, y2: pt.y + 16 };
          if (
            placed.some(
              (p) => !(box.x2 < p.x1 || box.x1 > p.x2 || box.y2 < p.y1 || box.y1 > p.y2)
            )
          ) {
            continue;
          }
          placed.push(box);
          const el = document.createElement("div");
          el.className = "hotple-area-pill";
          const nm = document.createElement("span");
          nm.className = "hap-nm";
          nm.textContent = s.n;
          const cnt = document.createElement("span");
          cnt.className = "hap-cnt";
          cnt.textContent = s.c;
          el.appendChild(nm);
          el.appendChild(cnt);
          el.addEventListener("click", (ev) => {
            ev.stopPropagation();
            const cb = callbacksRef.current.onAreaLabelClick;
            if (cb) {
              cb(s);
            } else {
              userInteractionRef.current = true;
              map.easeTo({
                center: [s.lng, s.lat],
                zoom:
                  callbacksRef.current.areaLabelClickZoom ??
                  (callbacksRef.current.areaLabelZoomMax ?? 15.5) + 1.6,
                duration: 500,
              });
            }
          });
          const mk = new maplibregl.Marker({ element: el, anchor: "center" })
            .setLngLat([s.lng, s.lat])
            .addTo(map);
          areaMarkersRef.current.push(mk);
        }
      };
      syncAreaLabelsRef.current = syncAreaLabels;
      const markMoving = () => {
        if (areaLabelsMovingRef.current) return;
        areaLabelsMovingRef.current = true;
        syncAreaLabels(); // 적은 개수로 다시 그린다
      };
      const markSettled = () => {
        areaLabelsMovingRef.current = false;
        syncAreaLabels(); // 전부 다시 그린다
      };
      map.on("movestart", markMoving);
      map.on("zoomstart", markMoving);
      map.on("moveend", markSettled);
      map.on("zoomend", markSettled);
      if (map.isStyleLoaded()) syncAreaLabels();
      else map.once("idle", syncAreaLabels);
    }

    map.on("click", PLACE_LAYER_ID, (event) => {
      const features = event.features;
      if (!features || !features.length) return;

      // Distinct places at (near-)identical coordinates render as overlapping
      // pin features. Taking features[0] blindly selected an ARBITRARY one — so
      // the pin you saw labeled A could resolve to co-located place B. Resolve to
      // the feature whose pin is actually nearest the click pixel so the
      // selection matches the pin the user tapped. (Co-located pins are fanned a
      // few metres apart in buildPlaceMapMarkers, so nearest is now meaningful.)
      let feature = features[0];
      if (features.length > 1 && event.point) {
        let nearestDist = Infinity;
        for (const candidate of features) {
          const coords = candidate?.geometry?.coordinates;
          if (!Array.isArray(coords)) continue;
          const projected = map.project(coords);
          const dist = Math.hypot(
            projected.x - event.point.x,
            projected.y - event.point.y
          );
          if (dist < nearestDist) {
            nearestDist = dist;
            feature = candidate;
          }
        }
      }

      callbacksRef.current.onMarkerClick?.(
        feature.properties,
        {
          lng: event.lngLat.lng,
          lat: event.lngLat.lat,
        },
        event.point ? { x: event.point.x, y: event.point.y } : null
      );
    });

    // Fallback pin hit-test from OUR feature data (see markersGeoJsonRef). Fires
    // on any map click. If MapLibre's rendered-feature hit-test already found a
    // pin/cluster (tiles ready), the layer/cluster handlers own it and we bail —
    // no double-fire. But during the ~5-7s after loading a NEW area, the pin
    // source tiles aren't built yet (the worker is busy parsing the basemap), so
    // queryRenderedFeatures is empty and a visibly-rendered pin can't be tapped.
    // In that window we project our own pin coordinates to screen and open the
    // nearest one — pins become tappable the moment their data is set.
    map.on("click", (event) => {
      const interactiveLayers = [
        PLACE_LAYER_ID,
        CLUSTER_LAYER_ID,
        CLUSTER_COUNT_LAYER_ID,
      ].filter((id) => map.getLayer(id));
      const rendered = interactiveLayers.length
        ? map.queryRenderedFeatures(event.point, { layers: interactiveLayers })
        : [];
      if (rendered.length) return;

      // Only apply the fallback where pins are INDIVIDUAL (past clusterMaxZoom=16).
      // At/below that, a tap should EXPAND the cluster it lands on, not open the
      // nearest underlying pin — leave those to the cluster handler once tiles
      // load. (Guards the short pre-tile window; at the individual-pin zooms the
      // user actually taps a specific pin, this is exactly what they want.)
      if (map.getZoom() <= 21) return;

      const features = markersGeoJsonRef.current?.features;
      if (!Array.isArray(features) || !features.length) return;
      let nearest = null;
      let nearestDist = Infinity;
      for (const feature of features) {
        const coords = feature?.geometry?.coordinates;
        if (!coords) continue;
        const projected = map.project(coords);
        const dist = Math.hypot(
          projected.x - event.point.x,
          projected.y - event.point.y
        );
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = feature;
        }
      }
      // ~28px ≈ the pin tap radius; ignore taps on empty map.
      if (nearest && nearestDist <= 28) {
        const coords = nearest.geometry.coordinates;
        callbacksRef.current.onMarkerClick?.(
          nearest.properties,
          { lng: coords[0], lat: coords[1] },
          { x: event.point.x, y: event.point.y }
        );
      }
    });

    let lastClusterTouchTime = 0;

    const handleClusterClick = (event) => {
      if (isSyntheticClickAfterTouch(event, lastClusterTouchTime)) return false;

      const feature = event.features?.[0] || getRenderedClusterFeature(map, event);
      const clusterId = feature?.properties?.cluster_id;
      const coordinates = feature?.geometry?.coordinates;
      const source = map.getSource(PLACES_SOURCE_ID);
      if (!source || clusterId == null || !Array.isArray(coordinates)) return false;

      const shouldDeferMove = isTouchMapEvent(event);

      source
        .getClusterExpansionZoom(clusterId)
        .then((zoom) => {
          if (!Number.isFinite(zoom)) return;

          const nextZoom = getClusterTargetZoom(map, zoom);
          const moveToCluster = () => {
            // Treat the cluster-expand zoom as a user interaction so the
            // post-zoom `moveend` fires onUserMoveEnd → the list/"이 지역 N곳"
            // refetch to the newly-visible (smaller) area. Without this the
            // programmatic easeTo leaves userInteractionRef false and the
            // moveend handler bails, so the count stays stale after a tap.
            userInteractionRef.current = true;
            map.easeTo({
              center: coordinates,
              zoom: nextZoom,
              duration: 450,
              essential: true,
            });
          };

          // MapLibre may cancel camera changes made synchronously during the
          // touchend gesture on iOS. Defer past the gesture so it isn't dropped.
          if (shouldDeferMove) {
            window.setTimeout(moveToCluster, 120);
            return;
          }

          moveToCluster();
        })
        .catch(() => {});

      return true;
    };

    const handleClusterTouchEnd = (event) => {
      // On a single-finger tap, touchend has 0 remaining points, so the old
      // `!== 1` check rejected EVERY tap (making cluster taps rely on a synthetic
      // click that iOS Safari fires unreliably → clusters "wouldn't expand").
      // Only skip genuine multi-touch (pinch) gestures.
      if (event?.points && event.points.length > 1) return;

      if (handleClusterClick(event)) {
        lastClusterTouchTime = Date.now();
        event.preventDefault();
      }
    };

    map.on("click", handleClusterClick);
    map.on("touchend", handleClusterTouchEnd);

    const setPointerCursor = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const clearPointerCursor = () => {
      map.getCanvas().style.cursor = "";
    };

    POINTER_LAYER_IDS.forEach((layerId) => {
      map.on("mouseenter", layerId, setPointerCursor);
      map.on("mouseleave", layerId, clearPointerCursor);
    });

    return () => {
      window.cancelAnimationFrame(resizeFrame);
      window.cancelAnimationFrame(initialCameraFrame);
      window.clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      // 지도 캔버스에 직접 건 손짓 리스너를 걷어낸다.
      if (gestureCleanupRef.current) {
        gestureCleanupRef.current();
        gestureCleanupRef.current = null;
      }
      map.remove();
      mapRef.current = null;
    };
  }, []);


  // 이름표 목록이 바뀌면 곧바로 다시 그린다.
  //
  // 예전에는 지도가 움직일 때만(moveend/zoomend) 다시 그렸다. 그래서 칩을 눌러 확대하면
  // **그 순간의 옛 목록**으로 그려지고, 잠시 뒤 새 목록(세부)이 도착해도 다시 그릴 일이
  // 없어서 큰 이름이 그대로 남았다 — 한 번 더 눌러야 넘어갔다(2026-08-07 사장님 제보).
  useEffect(() => {
    syncAreaLabelsRef.current?.();
  }, [areaLabels]);

  useEffect(() => {
    const map = mapRef.current;
    if (
      !map ||
      !isLoaded ||
      !trackedLocationOverlayElement ||
      !resolvedTrackedLocation
    ) {
      return undefined;
    }

    const marker = new maplibregl.Marker({
      anchor: "bottom",
      element: trackedLocationOverlayElement,
      offset: trackedLocationOverlayOffset,
      subpixelPositioning: true,
    })
      .setLngLat(resolvedTrackedLocation)
      .addTo(map);
    marker.setSubpixelPositioning?.(true);

    trackedLocationOverlayMarkerRef.current = marker;

    return () => {
      if (trackedLocationOverlayMarkerRef.current === marker) {
        trackedLocationOverlayMarkerRef.current = null;
      }
      marker.remove();
    };
  }, [
    isLoaded,
    resolvedTrackedLocation,
    trackedLocationOverlayElement,
    trackedLocationOverlayOffset,
  ]);

  useEffect(() => {
    const marker = trackedLocationOverlayMarkerRef.current;
    if (!marker) return;

    const element = marker.getElement();

    if (!resolvedTrackedLocation) {
      element.style.display = "none";
      return;
    }

    marker.setLngLat(resolvedTrackedLocation);
    element.style.display = "";
  }, [resolvedTrackedLocation]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;
    const source = map.getSource(PLACES_SOURCE_ID);
    source?.setData(markersGeoJson);
  }, [isLoaded, markersGeoJson]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;

    if (scrollZoom) {
      map.scrollZoom.enable();
      return;
    }

    map.scrollZoom.disable();
  }, [isLoaded, scrollZoom]);

  // 지하철 노선 선·역사 막대·출구를 지도에 깐다.
  //
  // 장소 핀 **밑에** 깐다(CLUSTER_LAYER_ID 앞) — 노선은 배경이고 주인공은 핀이다.
  // 화면이 움직여도 다시 넣지 않는다: 전국 한 벌이 이미 지도 안에 있고, 무엇을 그릴지는
  // MapLibre 가 알아서 고른다. 그래서 지도를 아무리 끌어도 요청이 생기지 않는다.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !subwayNetwork) return;
    try {
      installSubwayLayers(map, subwayNetwork, CLUSTER_LAYER_ID);
    } catch (error) {
      // 노선이 못 깔려도 지도는 그대로 쓴다.
    }
  }, [isLoaded, subwayNetwork]);

  useEffect(() => {
    const map = mapRef.current;
    if (
      !map ||
      !isLoaded ||
      !fitBoundsToMarkers ||
      fitBoundsCoordinates.length === 0
    ) {
      return;
    }

    const maxZoom = Number.isFinite(fitBoundsMaxZoom)
      ? fitBoundsMaxZoom
      : map.getMaxZoom();
    const padding = Number.isFinite(fitBoundsPadding)
      ? fitBoundsPadding
      : 96;

    map.stop();

    if (fitBoundsCoordinates.length === 1) {
      map.jumpTo({
        center: fitBoundsCoordinates[0],
        zoom: Math.min(map.getZoom(), maxZoom),
      });
      return;
    }

    const bounds = fitBoundsCoordinates.reduce(
      (nextBounds, coordinates) => nextBounds.extend(coordinates),
      new maplibregl.LngLatBounds(
        fitBoundsCoordinates[0],
        fitBoundsCoordinates[0]
      )
    );

    map.fitBounds(bounds, {
      padding,
      maxZoom,
      duration: cameraAnimationDuration,
    });
  }, [
    cameraAnimationDuration,
    fitBoundsCoordinates,
    fitBoundsCoordinatesKey,
    fitBoundsMaxZoom,
    fitBoundsPadding,
    fitBoundsRequestKey,
    fitBoundsToMarkers,
    isLoaded,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (
      !map ||
      !isLoaded ||
      !focusTrackedLocationOnChange ||
      !resolvedTrackedLocation
    ) {
      if (!resolvedTrackedLocation) focusedTrackedLocationRef.current = null;
      return;
    }

    const [lng, lat] = resolvedTrackedLocation;
    const zoom = Number.isFinite(trackedLocationFocusZoom)
      ? trackedLocationFocusZoom
      : map.getZoom();
    // Guard the auto-focus on the tracked LOCATION only (lng:lat), NOT the zoom.
    // Callers pass the live map zoom as trackedLocationFocusZoom, so including
    // zoom here made the key change on every mouse-wheel scroll → the effect
    // re-jumped the camera back to the tracked pin while the user was zooming
    // (and the tracked-location object churns a new reference on each map-pin
    // refresh during a drag). Keying on the coordinate value focuses each
    // location exactly once; scrolling/dragging with a card open no longer snaps
    // back. A genuinely NEW location still re-focuses (different lng:lat).
    const focusKey = `${lng}:${lat}`;

    if (focusedTrackedLocationRef.current === focusKey) return;

    // Mark this selection as handled so the effect evaluates exactly ONCE per
    // selection (a subsequent drag/scroll churns resolvedTrackedLocation but the
    // focusKey is unchanged, so it returns early above and never snaps back).
    focusedTrackedLocationRef.current = focusKey;

    // Naver/Kakao-style "reveal only if clipped, smooth" behavior (desktop),
    // gated behind trackedLocationFocusPadding so mobile stays center-on-select.
    if (trackedLocationFocusPadding) {
      const pad = trackedLocationFocusPadding;
      const canvas = map.getCanvas();
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const p = map.project(resolvedTrackedLocation);

      const minX = pad.left ?? 0;
      const maxX = w - (pad.right ?? 0);
      const minY = pad.top ?? 0;
      const maxY = h - (pad.bottom ?? 0);

      // Pin + its popup card already fully inside the safe rect → do NOT move.
      if (p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY) return;

      // Otherwise nudge the camera the MINIMUM amount to reveal it. Clamp the
      // pin's target screen point into the safe rect, then translate the whole
      // scene by (dx, dy). Panning so the world point currently at screen
      // (w/2 - dx, h/2 - dy) lands at the map center shifts every screen point
      // by (dx, dy): e.g. a pin too far left (p.x < minX ⇒ dx > 0) unprojects a
      // center to the LEFT of the current one, so the scene slides RIGHT and the
      // pin moves into view. Keep zoom unchanged on a nudge.
      const clampedX = Math.min(Math.max(p.x, minX), maxX);
      const clampedY = Math.min(Math.max(p.y, minY), maxY);
      const dx = clampedX - p.x;
      const dy = clampedY - p.y;

      map.stop();
      map.easeTo({
        center: map.unproject([w / 2 - dx, h / 2 - dy]),
        duration: 450,
      });
      return;
    }

    // Default / mobile: keep center-on-select, just make it smooth.
    map.stop();
    if (isFarCameraMove(map, resolvedTrackedLocation)) {
      map.jumpTo({ center: resolvedTrackedLocation, zoom });
      return;
    }
    map.easeTo({
      center: resolvedTrackedLocation,
      zoom,
      duration: 450,
    });
  }, [
    focusTrackedLocationOnChange,
    isLoaded,
    resolvedTrackedLocation,
    trackedLocationFocusZoom,
    trackedLocationFocusPadding,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;
    if (focusTrackedLocationOnChange && resolvedTrackedLocation) return;
    if (fitBoundsToMarkers && fitBoundsCoordinatesKey) return;

    const nextZoom = mapZoom ?? DEFAULT_MAP_ZOOM;
    const shouldJump = cameraJumpKey !== lastCameraJumpKeyRef.current;
    lastCameraJumpKeyRef.current = cameraJumpKey;

    if (
      !shouldJump &&
      isCameraEchoUpdate(emittedCameraHistoryRef.current, resolvedCenter, nextZoom)
    ) {
      return;
    }

    if (!shouldUpdateMapCamera(map, resolvedCenter, nextZoom)) return;

    // 시트가 덮는 만큼 목표를 위로 올려, **보이는 지도의 가운데**에 놓는다.
    const sheetOffset = getSheetCameraOffset(map);

    if (shouldJump || cameraAnimationDuration <= 0) {
      map.stop();
      map.easeTo({
        center: resolvedCenter,
        zoom: nextZoom,
        offset: sheetOffset,
        duration: 0,
      });
      return;
    }

    if (isFarCameraMove(map, resolvedCenter)) {
      map.stop();
      map.easeTo({
        center: resolvedCenter,
        zoom: nextZoom,
        offset: sheetOffset,
        duration: 0,
      });
      return;
    }

    map.easeTo({
      center: resolvedCenter,
      zoom: nextZoom,
      offset: sheetOffset,
      duration: cameraAnimationDuration,
    });
  }, [
    cameraAnimationDuration,
    cameraJumpKey,
    focusTrackedLocationOnChange,
    fitBoundsCoordinatesKey,
    fitBoundsToMarkers,
    isLoaded,
    mapZoom,
    resolvedCenter,
    resolvedTrackedLocation,
  ]);

  return (
    <div
      className={`relative h-full min-h-[360px] w-full overflow-hidden ${className}`}
      style={style}
      data-maplibre-root
      data-maplibre-pmtiles-url={resolvedPmtilesUrl}
    >
      <div
        ref={containerRef}
        className="absolute inset-0"
        style={{ height: "100%", width: "100%" }}
      />
      {mapError && (
        <div className="absolute left-3 top-3 z-10 max-w-sm rounded-md border border-red-200 bg-white/95 px-3 py-2 text-xs font-medium text-red-700 shadow">
          {mapError}
        </div>
      )}
    </div>
  );
}
